import React, { useEffect, useState, useCallback } from "react";
import { ScrollText, Mail, Smartphone, MessageSquare } from "lucide-react";
import { ccCard, ccInput } from "./care/ui";
import { LogDetailModal } from "./lib/LogDetail";

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_META = {
  email: { icon: Mail, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]" },
  sms: { icon: Smartphone, wrap: "bg-[#E2F4E1]", color: "text-[#2F6031]" },
  internal: { icon: MessageSquare, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]" },
};

const STATUS_STYLE = {
  sent: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
  failed: "bg-rose-50 border-rose-200 text-rose-500",
  queued: "bg-[#FFF2D1] border-[#F5E3B3] text-[#D9A93E]",
  read: "bg-[#E6F2FA] border-[#CDE7F5] text-[#5CB3DC]",
};

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function CommLog() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [channel, setChannel] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/comm-log${channel ? `?channel=${channel}` : ""}`, { credentials: "include" });
      if (res.ok) {
        const body = await res.json();
        setItems(body.items);
        setTotal(body.total);
      }
    } catch { /* keep previous */ }
  }, [channel]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className={`${ccCard} p-6`} data-testid="comm-log">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <ScrollText className="w-4 h-4 text-[#5CB3DC]" />
          </span>
          Communications Log
          <span className="text-[10px] font-bold text-[#9CA3AF]" data-testid="comm-log-total">{total} deliveries</span>
        </h3>
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className={`${ccInput} !w-auto text-xs`} data-testid="comm-log-filter">
          <option value="">All channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="internal">Internal</option>
        </select>
      </div>
      {items === null ? (
        <p className="text-xs text-[#9CA3AF] font-semibold">Loading deliveries…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="comm-log-empty">No messages logged yet — every outbound email, SMS and internal message will appear here.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map((m) => {
            const meta = CHANNEL_META[m.channel] || CHANNEL_META.internal;
            const Icon = meta.icon;
            return (
              <button key={m.id} onClick={() => setSelected(m)} className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-[#FFF9F2] border border-[#FCE6E9] hover:border-[#B3E0F2] transition-colors cursor-pointer dark:bg-[#273244] dark:border-[#374457]" data-testid={`comm-log-row-${m.id}`}>
                <span className={`w-8 h-8 rounded-full ${meta.wrap} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">
                    {m.redacted ? "Private conversation — participants only" : m.subject || m.body || "(no subject)"}
                    <span className="ml-2 px-1.5 py-0.5 bg-white/70 border border-[#FCE6E9] text-[8px] font-extrabold uppercase rounded-full text-[#9CA3AF]">{m.kind}</span>
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] font-medium truncate">
                    {m.sender} → {m.recipient} · {fmtTs(m.createdAt)}
                  </p>
                </div>
                <span className={`px-2 py-0.5 border text-[9px] font-extrabold uppercase rounded-full shrink-0 ${STATUS_STYLE[m.status] || STATUS_STYLE.queued}`} data-testid={`comm-log-status-${m.id}`}>
                  {m.status}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {selected && (
        <LogDetailModal
          title={`${selected.channel?.toUpperCase()} · ${selected.kind || "direct"}`}
          fields={[
            { label: "Status", value: selected.status },
            { label: "From", value: selected.sender },
            { label: "To", value: selected.recipient },
            { label: "Subject", value: selected.redacted ? "Hidden — participants only" : selected.subject || "—" },
            { label: "Full message", value: selected.redacted ? "Hidden — only the people in this conversation can read it." : selected.body || "—" },
            { label: "Sent at", value: selected.createdAt ? new Date(selected.createdAt).toLocaleString() : "—" },
            { label: "Provider", value: selected.provider?.name || "—" },
            { label: "Provider ID", value: selected.provider?.id || selected.provider?.error || "—" },
          ]}
          json={selected.meta && Object.keys(selected.meta).length ? selected.meta : null}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
